import React from 'react';
import { Typography, Box, Divider } from '@mui/material';
import styled from '@emotion/styled';
import RestoreIcon from '@mui/icons-material/Restore';
import OnboardingVideoPlayer from 'components/v3/onboarding/OnboardingVideoPlayer';

// ==========================================
// Atomes de style partages entre tous les profils de tutoriel (cf. OnboardingContent.js)
// ==========================================

export const SlideContainer = styled(Box)({
    padding: '2em 1em',
    maxHeight: '60vh',
    overflowY: 'auto',
    overflowX: 'hidden',
});

// `component="div"` volontaire : le titre contient une pastille d'icone (un <div>),
// que le <p> rendu par defaut par Typography ne peut pas contenir — React emettait
// un avertissement validateDOMNesting a chaque ouverture du tutoriel.
export const SlideTitle = styled((props) => <Typography component="div" {...props} />)(({ theme }) => ({
    fontFamily: 'Inter, Arial, sans-serif',
    fontWeight: 700,
    fontSize: 'clamp(20px, 2vw, 28px)',
    color: theme?.palette?.primary?.main || '#0072E4',
    marginBottom: '0.5em',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5em',
}));

export const SlideSubtitle = styled(Typography)(({ theme }) => ({
    fontFamily: 'Inter, Arial, sans-serif',
    fontWeight: 600,
    fontSize: 'clamp(16px, 1.5vw, 20px)',
    color: theme?.palette?.text?.primary || '#333',
    marginTop: '1.5em',
    marginBottom: '0.8em',
}));

export const SlideText = styled(Typography)(({ theme }) => ({
    fontFamily: 'Inter, Arial, sans-serif',
    fontWeight: 400,
    fontSize: 'clamp(14px, 1.2vw, 16px)',
    color: theme?.palette?.text?.secondary || '#666',
    lineHeight: 1.6,
    marginBottom: '1em',
}));

export const BulletList = styled('ul')({
    marginLeft: '1.5em',
    marginBottom: '1em',
});

export const BulletItem = styled('li')(({ theme }) => ({
    fontFamily: 'Inter, Arial, sans-serif',
    fontSize: 'clamp(14px, 1.2vw, 16px)',
    color: theme?.palette?.text?.secondary || '#666',
    lineHeight: 1.8,
    marginBottom: '0.5em',
}));

export const HighlightBox = styled(Box)(({ theme }) => ({
    backgroundColor: '#F0F8FF',
    padding: '1em',
    borderRadius: '10px',
    borderLeft: `4px solid ${theme?.palette?.primary?.main || '#0072E4'}`,
    marginTop: '1em',
    marginBottom: '1em',
}));

export const StyledDivider = styled(Divider)({
    margin: '2em 0',
});

export const IconCircle = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: theme?.palette?.primary?.main || '#0072E4',
    color: 'white',
}));

// ==========================================
// Slides communes a tous les profils
// ==========================================

/**
 * Slide d'accueil. Le texte d'intro change selon le profil : on annonce a
 * l'utilisateur les pages qu'il va reellement voir, pas celles des autres.
 */
export const welcomeSlide = (intro) => ({
    title: 'Bienvenue sur MOBIUSpack 👋',
    content: (
        <SlideContainer>
            <SlideTitle>Bienvenue sur la nouvelle plateforme MOBIUSpack 👋</SlideTitle>
            <SlideText>
                Le dashboard MOBIUSpack a été entièrement réorganisé. Ce guide vous présente
                <strong> les pages auxquelles vous avez accès</strong> et ce que vous pouvez y faire.
            </SlideText>
            <SlideText>{intro}</SlideText>
            <HighlightBox>
                <SlideText sx={{ marginBottom: 0 }}>
                    💡 Vous pouvez fermer ce guide à tout moment et le rouvrir depuis le menu, entrée <strong>Tutoriel</strong>.
                </SlideText>
            </HighlightBox>
        </SlideContainer>
    ),
});

export const videoSlide = () => ({
    title: 'Découvrez MOBIUSpack en vidéo',
    content: (
        <SlideContainer>
            <SlideTitle>Découvrez MOBIUSpack en vidéo</SlideTitle>
            <SlideText>
                Pour une vue d'ensemble de la plateforme, regardez cette présentation vidéo qui vous guide à travers les principales fonctionnalités.
            </SlideText>
            <Box sx={{ marginTop: '2em', marginBottom: '2em' }}>
                <OnboardingVideoPlayer autoplay={false} muted={true} />
            </Box>
        </SlideContainer>
    ),
});

/**
 * Page Historique — visible par tous les profils (cf. route 'historic').
 *
 * @param {number} index - Numero de la slide dans le parcours du profil.
 * @param {boolean} [showExport] - L'export/import de donnees court-circuite le filtrage
 *                                 par lieu : masque pour les roles a perimetre restreint
 *                                 (store-manager, warehouse-manager, group-manager), cf.
 *                                 LOCATION_SCOPED_ROLES dans HistoricContext.js.
 */
export const historicSlide = (index, showExport = true) => ({
    title: `${index} · Historique`,
    content: (
        <SlideContainer>
            <SlideTitle>
                <IconCircle><RestoreIcon /></IconCircle>
                {index} · Historique
            </SlideTitle>
            <SlideText>
                Comme à votre habitude, retrouvez le <strong>détail de tous les scans</strong> sur la période de votre choix.
            </SlideText>

            <BulletList>
                <BulletItem>
                    <strong>Historique des événements</strong> — Quelques compteurs (envois, réceptions), le graphique des scans rangés par catégorie,
                    ainsi que le tableau détaillé de tous vos événements en dessous (avec l'historique par emballage).
                </BulletItem>
                <BulletItem>
                    <strong>Sélection de la plage de dates</strong> — choisissez la période pour laquelle vous souhaitez voir les données.
                </BulletItem>
                {showExport && (
                    <BulletItem>
                        <strong>Export de données</strong> — téléchargez les données en Excel via le bouton « Export de données ».
                    </BulletItem>
                )}
            </BulletList>

            <SlideText>
                En haut de page, vous filtrez par <strong>lieu</strong>, par <strong>flux</strong>, par{' '}
                <strong>référence d'emballage</strong> et par <strong>plage de dates</strong>.
            </SlideText>
            <HighlightBox>
                <SlideText sx={{ marginBottom: 0 }}>
                    🔍 Pour avoir l'historique par emballage, cliquez sur une ligne de la table des événements en bas.
                </SlideText>
            </HighlightBox>
            <HighlightBox>
                <SlideText sx={{ marginBottom: 0 }}>
                    📅 L'historique couvre <strong>12 mois glissants</strong>, avec un archivage sur 10 ans.
                </SlideText>
            </HighlightBox>
        </SlideContainer>
    ),
});

/**
 * @param {React.ReactNode} [extraNote] - Encart optionnel, propre au profil (ex: la page
 *                                        Conformite ESG, visible des seuls roles a large acces).
 */
export const finalSlide = (extraNote = null) => ({
    title: 'Vous êtes prêt !',
    content: (
        <SlideContainer>
            <SlideTitle>Vous êtes prêt ! 🚀</SlideTitle>
            <SlideText>
                Explorez chaque page à votre rythme. Vous avez maintenant les clés pour piloter votre flotte
                d'emballages réemployables.
            </SlideText>

            {extraNote}

            <StyledDivider />

            <SlideSubtitle>Trois choses à savoir avant de commencer</SlideSubtitle>
            <BulletList>
                <BulletItem>
                    Les chiffres se rafraîchissent <strong>toutes les minutes environ</strong>. Si vous venez de scanner,
                    laissez-lui le temps d'arriver avant de conclure à une erreur.
                </BulletItem>
                <BulletItem>
                    Certaines cartes affichent <strong>« donnée non disponible dans votre configuration »</strong> : ce n'est pas une panne. Elles
                    attendent la connexion avec vos transporteurs, qui complètera les informations nécessaires pour avoir cette donnée.
                </BulletItem>
            </BulletList>

            <StyledDivider />

            <SlideText>
                <strong>Une question ?</strong> Notre équipe est à votre disposition pour vous accompagner.
            </SlideText>
            <SlideText>
                Contactez-nous à <strong style={{ color: '#0072E4' }}>tech@mobiuspack.com</strong>
            </SlideText>

            <HighlightBox>
                <SlideText sx={{ marginBottom: 0 }}>
                    💡 Vous pouvez relancer ce tutoriel à tout moment depuis le menu, entrée <strong>Tutoriel</strong>.
                </SlideText>
            </HighlightBox>
        </SlideContainer>
    ),
});
